import { useState, useEffect, useRef, useMemo } from 'react'
import { Search, Truck, AlertTriangle, Wrench, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn, damagePhaseConfig } from '@fleetops/ui'
import { damagePhaseOf } from '@fleetops/types'
import { useDamages } from '@/features/damages/hooks/useDamages'
import { useTrailers } from '@/features/trailers/hooks/useTrailers'
import { useMaintenanceRecords } from '@/features/maintenance/hooks/useMaintenanceRecords'

interface SearchResult {
  id: string
  type: 'trailer' | 'damage' | 'maintenance'
  title: string
  subtitle: string
  action: () => void
}

const TYPE_CONFIG = {
  trailer:     { icon: <Truck className="h-4 w-4" />,         label: 'Reboque',    color: 'text-blue-500 bg-blue-50' },
  damage:      { icon: <AlertTriangle className="h-4 w-4" />, label: 'Avaria',     color: 'text-orange-500 bg-orange-50' },
  maintenance: { icon: <Wrench className="h-4 w-4" />,        label: 'Manutenção', color: 'text-slate-500 bg-slate-100' },
}

function normalize(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function matches(haystack: string, needle: string) {
  return normalize(haystack).includes(normalize(needle))
}

export function GlobalSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { trailers } = useTrailers()
  const { damages } = useDamages()
  const { records } = useMaintenanceRecords()

  useEffect(() => { inputRef.current?.focus() }, [])

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim()
    if (q.length < 2) return []

    const out: SearchResult[] = []

    trailers.forEach((t) => {
      if (matches(t.plateNumber, q) || matches(t.make ?? '', q) || matches(t.model ?? '', q)) {
        out.push({
          id: t.id,
          type: 'trailer',
          title: t.plateNumber,
          subtitle: [t.make, t.model, t.year].filter(Boolean).join(' ') || 'Reboque',
          action: () => { navigate(`/trailers#${t.id}`); onClose() },
        })
      }
    })

    damages.forEach((d) => {
      if (matches(d.title, q) || matches(d.trailerPlate, q) || matches(d.description ?? '', q)) {
        out.push({
          id: d.id,
          type: 'damage',
          title: d.title,
          subtitle: `${d.trailerPlate} · ${damagePhaseConfig[damagePhaseOf(d.status)].label}`,
          action: () => { navigate(`/damages/${d.id}`); onClose() },
        })
      }
    })

    records.forEach((r) => {
      if (matches(r.title, q) || matches(r.trailerPlate, q) || matches(r.technicianName ?? '', q)) {
        out.push({
          id: r.id,
          type: 'maintenance',
          title: r.title,
          subtitle: `${r.trailerPlate} · ${r.status}`,
          action: () => { navigate(`/maintenance#${r.id}`); onClose() },
        })
      }
    })

    return out.slice(0, 12)
  }, [query, trailers, damages, records, navigate, onClose])

  useEffect(() => { setCursor(0) }, [results])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((c) => Math.min(c + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((c) => Math.max(c - 1, 0))
    } else if (e.key === 'Enter' && results[cursor]) {
      results[cursor].action()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    const el = listRef.current?.children[cursor] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  const grouped = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {}
    for (const r of results) {
      if (!groups[r.type]) groups[r.type] = []
      groups[r.type]!.push(r)
    }
    return groups
  }, [results])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20 px-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pesquisar reboques, avarias, manutenções..."
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
          <kbd className="hidden rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-400 sm:block">Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-96 overflow-y-auto py-2">
          {query.length < 2 && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              Escreve pelo menos 2 caracteres para pesquisar
            </p>
          )}

          {query.length >= 2 && results.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              Sem resultados para <span className="font-medium text-slate-600">"{query}"</span>
            </p>
          )}

          {(Object.entries(grouped) as [keyof typeof TYPE_CONFIG, SearchResult[]][]).map(([type, items]) => {
            const cfg = TYPE_CONFIG[type]
            return (
              <div key={type} className="mb-1">
                <p className="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  {cfg.label}
                </p>
                {items.map((item) => {
                  const idx = results.indexOf(item)
                  return (
                    <button
                      key={item.id}
                      onClick={item.action}
                      onMouseEnter={() => setCursor(idx)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        cursor === idx ? 'bg-blue-50' : 'hover:bg-slate-50',
                      )}
                    >
                      <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', cfg.color)}>
                        {cfg.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{item.title}</p>
                        <p className="truncate text-xs text-slate-500">{item.subtitle}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-4 py-2">
          <span className="text-[10px] text-slate-400">
            <kbd className="rounded border border-slate-200 px-1 py-0.5 text-[9px]">↑↓</kbd> navegar &nbsp;
            <kbd className="rounded border border-slate-200 px-1 py-0.5 text-[9px]">↵</kbd> abrir &nbsp;
            <kbd className="rounded border border-slate-200 px-1 py-0.5 text-[9px]">Esc</kbd> fechar
          </span>
          {results.length > 0 && (
            <span className="ml-auto text-[10px] text-slate-400">{results.length} resultado{results.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>
  )
}
